package com.presentocracy.web

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.presentocracy.shared.contract.CreateGiftRequest
import com.presentocracy.shared.contract.CreateWishlistRequest
import com.presentocracy.shared.contract.LoginRequest
import com.presentocracy.shared.contract.RegisterRequest
import com.presentocracy.shared.contract.UpdateProfileRequest
import com.presentocracy.shared.contract.UpdateWishlistRequest
import com.presentocracy.shared.contract.AddContributionRequest
import com.presentocracy.shared.contract.EnableGroupGiftRequest
import com.presentocracy.shared.contract.ProductMetadataResponse
import com.presentocracy.shared.contract.WishlistAclRequest
import com.presentocracy.shared.model.Birthday
import com.presentocracy.shared.model.BirthdaySummary
import com.presentocracy.shared.model.BaseVisibility
import com.presentocracy.shared.model.AccessOverride
import com.presentocracy.shared.model.ReservationInfo
import com.presentocracy.shared.model.ReservationStatus
import com.presentocracy.shared.model.ReservationVisibility
import com.presentocracy.shared.contract.GroupGiftResponse
import com.presentocracy.shared.model.Friendship
import com.presentocracy.shared.model.FriendshipStatus
import com.presentocracy.shared.model.Gift
import com.presentocracy.shared.model.UserCard
import com.presentocracy.shared.model.UserProfile
import com.presentocracy.shared.model.Wishlist
import com.presentocracy.shared.network.ApiClientException
import com.presentocracy.shared.network.PresentocracyApiClient
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.cancel
import kotlinx.coroutines.launch
import kotlinx.coroutines.Job
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.browser.window
import com.presentocracy.shared.contract.EmailVerificationWindow
import com.presentocracy.shared.contract.parseWishlistNavigationId

sealed interface WebAppState {
    object Loading : WebAppState
    object SignedOut : WebAppState
    data class SignedIn(
        val user: UserProfile,
        val wishlists: List<Wishlist>,
        val birthdays: List<BirthdaySummary>,
        val friends: List<Friendship>,
    ) : WebAppState
}

enum class WebAuthMode { LOGIN, REGISTER }
enum class WebTab { HOME, WISHLISTS, FRIENDS, BIRTHDAYS, PROFILE, SETTINGS }

class WebController {
    var linkedWishlistId by mutableStateOf(parseWishlistNavigationId(js("new URLSearchParams(window.location.search).get('wishlist_id')") as String?))
        private set
    var linkedWishlist by mutableStateOf<Wishlist?>(null)
        private set
    var linkedWishlistLoading by mutableStateOf(false)
        private set
    var linkedWishlistError by mutableStateOf(false)
        private set
    private var resendTimer: Job? = null
    private var linkRequestVersion = 0L
    private var resendEmail: String? = null
    private var resendSeconds by mutableStateOf(0L)

    fun resendWaitSeconds(email: String): Long = if (email.trim().lowercase() == resendEmail) resendSeconds else 0
    fun resendCaption(email: String): String {
        val seconds = resendWaitSeconds(email)
        return if (seconds > 0) "Отправить снова через ${ (seconds / 60).toString().padStart(2, '0') }:${ (seconds % 60).toString().padStart(2, '0') }"
        else "Отправить код повторно"
    }
    private fun applyVerificationWindow(email: String, verification: EmailVerificationWindow?) {
        resendTimer?.cancel()
        resendEmail = email.trim().lowercase()
        val duration = verification?.let { (it.resendAvailableAtEpochMillis - it.serverTimeEpochMillis).coerceAtLeast(0L) } ?: 0L
        val deadline = window.performance.now() + duration.toDouble()
        resendSeconds = (duration + 999L) / 1000L
        resendTimer = scope.launch {
            while (resendSeconds > 0) {
                delay(250)
                resendSeconds = kotlin.math.ceil((deadline - window.performance.now()).coerceAtLeast(0.0) / 1000.0).toLong()
            }
        }
    }
    private suspend fun loadLinkedWishlist() {
        val id = linkedWishlistId ?: return
        val version = ++linkRequestVersion
        linkedWishlistLoading = true
        linkedWishlist = null
        linkedWishlistError = false
        try {
            val result = api.wishlistFromNavigation(id)
            if (version == linkRequestVersion && id == linkedWishlistId) linkedWishlist = result
        }
        catch (error: Throwable) {
            if (error is CancellationException) throw error
            if (version == linkRequestVersion) linkedWishlistError = true
        }
        finally { if (version == linkRequestVersion) linkedWishlistLoading = false }
    }
    fun retryWishlistLink() { scope.launch { loadLinkedWishlist() } }
    fun closeWishlistLink() {
        linkRequestVersion++
        linkedWishlistLoading = false
        linkedWishlistId = null
        linkedWishlist = null
        js("window.history.replaceState({}, document.title, window.location.pathname)")
    }
    private val api = PresentocracyApiClient(webApiBaseUrl())
    private val scope = MainScope()
    private val storage: dynamic = js("window.localStorage")
    private var started = false

    var state by mutableStateOf<WebAppState>(WebAppState.Loading)
        private set
    var authMode by mutableStateOf(WebAuthMode.LOGIN)
    var verificationEmail by mutableStateOf<String?>(null)
        private set
    var selectedTab by mutableStateOf(WebTab.HOME)
    var selectedWishlistId by mutableStateOf<String?>(null)
    var wishlistDetailOpen by mutableStateOf(false)
        private set
    var showCreateWishlist by mutableStateOf(false)
    var showCreateGift by mutableStateOf(false)
    var showEditWishlist by mutableStateOf(false)
    var shareUrl by mutableStateOf<String?>(null)
        private set
    var friendSearchResults by mutableStateOf<List<UserCard>>(emptyList())
        private set
    var viewedUser by mutableStateOf<UserCard?>(null)
        private set
    var viewedUserWishlists by mutableStateOf<List<Wishlist>>(emptyList())
        private set
    var message by mutableStateOf<String?>(null)
        private set
    var isBusy by mutableStateOf(false)
        private set
    var reservationByGiftId by mutableStateOf<Map<String, ReservationInfo?>>(emptyMap())
        private set
    var groupByGiftId by mutableStateOf<Map<String, GroupGiftResponse>>(emptyMap())
        private set
    var coordinationBusyGiftId by mutableStateOf<String?>(null)
        private set
    private var loadedCoordinationGiftIds by mutableStateOf<Set<String>>(emptySet())

    fun start() {
        if (started) return
        started = true
        val verificationToken = readVerificationTokenFromUrl()
        if (!verificationToken.isNullOrBlank()) {
            clearVerificationTokenFromUrl()
            state = WebAppState.SignedOut
            isBusy = true
            scope.launch {
                runCatching { api.verifyEmail(verificationToken) }
                    .onSuccess { message = "Email подтверждён. Теперь войдите." }
                    .onFailure { message = readableError(it) }
                isBusy = false
            }
            return
        }
        val refreshToken = readRefreshToken()
        if (refreshToken.isNullOrBlank()) {
            state = WebAppState.SignedOut
            retryWishlistLink()
            return
        }
        isBusy = true
        scope.launch {
            runCatching {
                val session = api.refresh(refreshToken)
                saveRefreshToken(session.refreshToken)
                loadSignedIn()
            }.onFailure {
                clearSession()
                state = WebAppState.SignedOut
                retryWishlistLink()
            }
            isBusy = false
        }
    }

    fun clearMessage() {
        message = null
    }

    fun verifyEmailCode(code: String) {
        val email = verificationEmail ?: return
        launchRequest {
            api.verifyEmailCode(email, code.trim())
            verificationEmail = null
            authMode = WebAuthMode.LOGIN
            message = "Email подтверждён. Теперь войдите."
        }
    }

    fun cancelEmailVerification() {
        verificationEmail = null
        message = null
    }

    fun submitAuth(email: String, password: String, username: String, displayName: String) {
        message = null
        launchRequest {
            try {
                if (authMode == WebAuthMode.LOGIN) {
                    val session = api.login(LoginRequest(email.trim(), password))
                    saveRefreshToken(session.refreshToken)
                    loadSignedIn()
                } else {
                    val registration = api.register(
                        RegisterRequest(email.trim(), password, username.trim(), displayName.trim()),
                    )
                    val verificationToken = registration.verificationToken
                    if (verificationToken != null) {
                        api.verifyEmail(verificationToken)
                        val session = api.login(LoginRequest(email.trim(), password))
                        saveRefreshToken(session.refreshToken)
                        loadSignedIn()
                    } else {
                        verificationEmail = email.trim()
                        applyVerificationWindow(email, registration.verification)
                        message = null
                    }
                }
            } catch (error: Throwable) {
                if ((error as? ApiClientException)?.error?.code == "EMAIL_NOT_VERIFIED") {
                    verificationEmail = email.trim()
                }
                throw error
            }
        }
    }

    fun signInWithGoogle(idToken: String) {
        if (idToken.isBlank()) {
            message = "Google не вернул данные для входа. Попробуйте ещё раз."
            return
        }
        launchRequest {
            val session = api.loginWithGoogle(idToken)
            saveRefreshToken(session.refreshToken)
            loadSignedIn()
        }
    }

    fun resendVerification(email: String) {
        if (resendWaitSeconds(email) > 0) return
        if (email.trim().isBlank()) {
            message = "Введите email"
            return
        }
        launchRequest {
            val response = api.resendVerification(email.trim())
            applyVerificationWindow(email, response.verification)
            message = "Если аккаунт существует и ещё не подтверждён, новое письмо отправлено."
        }
    }

    fun logout() {
        if (isBusy) return
        val refreshToken = readRefreshToken()
        isBusy = true
        scope.launch {
            runCatching { if (!refreshToken.isNullOrBlank()) api.logout(refreshToken) }
            disableGoogleAutoSelect()
            clearSession()
            state = WebAppState.SignedOut
            selectedTab = WebTab.HOME
            selectedWishlistId = null
            wishlistDetailOpen = false
            shareUrl = null
            friendSearchResults = emptyList()
            viewedUser = null
            viewedUserWishlists = emptyList()
            reservationByGiftId = emptyMap()
            groupByGiftId = emptyMap()
            loadedCoordinationGiftIds = emptySet()
            message = null
            verificationEmail = null
            isBusy = false
        }
    }

    fun deleteAccount() {
        launchRequest {
            api.deleteAccount()
            clearSession()
            state = WebAppState.SignedOut
            selectedTab = WebTab.HOME
            message = null
        }
    }

    fun changePassword(currentPassword: String, newPassword: String, onSuccess: () -> Unit = {}) {
        if (isBusy) return
        isBusy = true
        message = null
        scope.launch {
            runCatching { api.changePassword(currentPassword, newPassword) }
                .onSuccess {
                    message = "Пароль изменён"
                    onSuccess()
                }
                .onFailure { message = readableError(it) }
            isBusy = false
        }
    }

    fun refresh() {
        if (state !is WebAppState.SignedIn) return
        launchRequest { loadSignedIn() }
    }

    fun searchFriends(query: String) {
        val normalized = query.trim()
        if (normalized.length < 2) {
            friendSearchResults = emptyList()
            message = "Введите минимум 2 символа для поиска"
            return
        }
        launchRequest {
            friendSearchResults = api.searchUsers(normalized)
            message = if (friendSearchResults.isEmpty()) "Никого не нашли" else null
        }
    }

    fun sendFriendRequest(userId: String) {
        launchRequest {
            val created = api.sendFriendRequest(userId)
            val current = requireSignedIn()
            state = current.copy(friends = current.friends.upsertFriendship(created))
            message = "Запрос в друзья отправлен"
        }
    }

    fun respondToFriendRequest(friendshipId: String, status: FriendshipStatus) {
        launchRequest {
            val updated = api.respondToFriendRequest(friendshipId, status)
            if (status == FriendshipStatus.ACCEPTED) {
                loadSignedIn()
            } else {
                val current = requireSignedIn()
                state = current.copy(friends = current.friends.map { if (it.id == updated.id) updated else it })
            }
            message = if (status == FriendshipStatus.ACCEPTED) "Теперь вы друзья" else "Запрос отклонён"
        }
    }

    fun openUserWishlists(user: UserCard) {
        launchRequest {
            viewedUserWishlists = api.wishlistsForUser(user.id)
            viewedUser = user
            selectedTab = WebTab.FRIENDS
            message = if (viewedUserWishlists.isEmpty()) {
                "У пользователя пока нет доступных списков"
            } else {
                null
            }
        }
    }

    fun closeUserWishlists() {
        viewedUser = null
        viewedUserWishlists = emptyList()
    }

    fun openWishlistDetails(wishlistId: String) {
        selectedWishlistId = wishlistId
        wishlistDetailOpen = true
        showCreateWishlist = false
        showCreateGift = false
        showEditWishlist = false
    }

    fun closeWishlistDetails() {
        wishlistDetailOpen = false
        showCreateGift = false
        showEditWishlist = false
    }

    fun createWishlist(slug: String, name: String, description: String, baseVisibility: BaseVisibility, reservationVisibility: ReservationVisibility) {
        launchRequest {
            val created = api.createWishlist(
                CreateWishlistRequest(
                    slug = slug.trim().ifEmpty { null },
                    name = name.trim(),
                    description = description.trim().ifEmpty { null },
                    baseVisibility = baseVisibility,
                    reservationVisibility = reservationVisibility,
                ),
            )
            val current = requireSignedIn()
            state = current.copy(wishlists = current.wishlists + created)
            selectedWishlistId = created.id
            showCreateWishlist = false
            wishlistDetailOpen = true
            message = "Список создан"
        }
    }

    fun updateWishlist(
        wishlistId: String,
        slug: String,
        name: String,
        description: String,
        baseVisibility: BaseVisibility,
        reservationVisibility: ReservationVisibility,
    ) {
        launchRequest {
            val updated = api.updateWishlist(
                wishlistId,
                UpdateWishlistRequest(
                    slug = slug.trim().ifEmpty { null },
                    name = name.trim(),
                    description = description.trim().ifEmpty { null },
                    baseVisibility = baseVisibility,
                    reservationVisibility = reservationVisibility,
                ),
            )
            val current = requireSignedIn()
            state = current.copy(wishlists = current.wishlists.map { if (it.id == updated.id) updated else it })
            showEditWishlist = false
            message = "Список обновлён"
        }
    }

    fun createGift(wishlistId: String, title: String, description: String, price: String, currency: String, productUrl: String = "") {
        launchRequest {
            val priceMinorUnits = price.trim().takeIf { it.isNotEmpty() }?.let(::parseMinorUnits)
            val gift = api.createGift(
                wishlistId,
                CreateGiftRequest(
                    title = title.trim(),
                    description = description.trim().ifEmpty { null },
                    productUrl = productUrl.trim().ifEmpty { null },
                    priceMinorUnits = priceMinorUnits,
                    priceCurrency = priceMinorUnits?.let { currency.trim().uppercase() },
                ),
            )
            val current = requireSignedIn()
            state = current.copy(wishlists = current.wishlists.replaceGift(wishlistId, gift))
            showCreateGift = false
            message = "Подарок добавлен"
        }
    }

    fun lookupProductMetadata(url: String, onResult: (ProductMetadataResponse) -> Unit) {
        if (url.trim().isEmpty() || isBusy) return
        isBusy = true
        message = null
        scope.launch {
            try {
                onResult(api.parseProductMetadata(url.trim()))
            } catch (error: Throwable) {
                message = readableError(error)
            } finally {
                isBusy = false
            }
        }
    }

    fun createShareLink(wishlistId: String) {
        launchRequest {
            val result = api.createShareLink(wishlistId)
            shareUrl = result.url
            message = "Ссылка создана — её можно открыть или скопировать."
        }
    }

    fun grantWishlistAccess(wishlistId: String, userId: String) {
        launchRequest {
            api.setWishlistAcl(wishlistId, WishlistAclRequest(userId, AccessOverride.ALLOW))
            message = "Доступ к списку открыт"
        }
    }

    fun loadCoordination(giftId: String) {
        if (giftId in loadedCoordinationGiftIds || coordinationBusyGiftId != null) return
        loadedCoordinationGiftIds = loadedCoordinationGiftIds + giftId
        coordinationBusyGiftId = giftId
        scope.launch {
            try {
                val reservation = api.reservation(giftId).reservation
                val group = api.groupGift(giftId)
                reservationByGiftId = reservationByGiftId + (giftId to reservation)
                groupByGiftId = groupByGiftId + (giftId to group)
            } catch (error: Throwable) {
                loadedCoordinationGiftIds = loadedCoordinationGiftIds - giftId
                message = readableError(error)
            } finally {
                coordinationBusyGiftId = null
            }
        }
    }

    fun reservationFor(giftId: String): ReservationInfo? = reservationByGiftId[giftId]

    fun groupFor(giftId: String): GroupGiftResponse? = groupByGiftId[giftId]

    fun reserveGift(giftId: String) = coordinationRequest(giftId) {
        reservationByGiftId = reservationByGiftId + (giftId to api.reserveGift(giftId).reservation)
        message = "Подарок зарезервирован"
    }

    fun cancelReservation(giftId: String) = coordinationRequest(giftId) {
        api.cancelGiftReservation(giftId)
        reservationByGiftId = reservationByGiftId + (giftId to null)
        message = "Резерв снят"
    }

    fun completeReservation(giftId: String, status: ReservationStatus) = coordinationRequest(giftId) {
        reservationByGiftId = reservationByGiftId + (giftId to api.updateReservationStatus(giftId, status).reservation)
        message = if (status == ReservationStatus.PURCHASED) "Отмечено как купленное" else "Отмечено как подаренное"
    }

    fun enableGroupGift(giftId: String, target: String, currency: String) = coordinationRequest(giftId) {
        val targetMinor = target.trim().takeIf { it.isNotEmpty() }?.let(::parseMinorUnits)
        groupByGiftId = groupByGiftId + (giftId to api.enableGroupGift(giftId, EnableGroupGiftRequest(targetMinor, currency.trim().uppercase().ifEmpty { null })))
        message = "Групповой подарок включён"
    }

    fun addContribution(giftId: String, amount: String, currency: String) = coordinationRequest(giftId) {
        val amountMinor = parseMinorUnits(amount)
        groupByGiftId = groupByGiftId + (giftId to api.addContribution(giftId, AddContributionRequest(amountMinor, currency.trim().uppercase())))
        message = "Взнос добавлен"
    }

    private fun coordinationRequest(giftId: String, action: suspend () -> Unit) {
        if (coordinationBusyGiftId != null) return
        coordinationBusyGiftId = giftId
        scope.launch {
            try {
                action()
            } catch (error: Throwable) {
                message = readableError(error)
            } finally {
                coordinationBusyGiftId = null
            }
        }
    }

    fun saveProfile(
        displayName: String,
        bio: String,
        preferredCurrency: String,
        birthdayInput: String,
        birthdayYearKnown: Boolean,
        showBirthYear: Boolean,
    ) {
        launchRequest {
            val birthday = parseBirthdayInput(birthdayInput, birthdayYearKnown)
            val updated = api.updateProfile(
                UpdateProfileRequest(
                    displayName = displayName.trim(),
                    bio = bio.trim(),
                    preferredCurrency = preferredCurrency.trim().uppercase(),
                    birthday = birthday,
                    showBirthYear = showBirthYear,
                ),
            )
            val current = requireSignedIn()
            state = current.copy(user = updated)
            message = "Профиль сохранён"
        }
    }

    private fun launchRequest(action: suspend () -> Unit) {
        if (isBusy) return
        isBusy = true
        message = null
        scope.launch {
            try {
                action()
            } catch (error: Throwable) {
                message = readableError(error)
            } finally {
                isBusy = false
            }
        }
    }

    private suspend fun loadSignedIn() {
        val user = api.currentUser()
        val wishlists = api.wishlists()
        val birthdays = runCatching { api.upcomingBirthdays() }.getOrNull()?.birthdays.orEmpty()
        val friends = api.friendships()
        state = WebAppState.SignedIn(user, wishlists, birthdays, friends)
        if (selectedWishlistId !in wishlists.map { it.id }) {
            selectedWishlistId = null
            wishlistDetailOpen = false
        }
        message = null
        loadLinkedWishlist()
    }

    private fun requireSignedIn(): WebAppState.SignedIn = state as? WebAppState.SignedIn
        ?: error("Пользователь не авторизован")

    private fun saveRefreshToken(token: String) {
        storage.setItem(STORAGE_KEY, token)
    }

    private fun readRefreshToken(): String? {
        val value = storage.getItem(STORAGE_KEY)
        return if (value == null) null else value.toString()
    }

    private fun readVerificationTokenFromUrl(): String? =
        js("new URLSearchParams(window.location.search).get('verification_token')") as String?

    private fun clearVerificationTokenFromUrl() {
        js("window.history.replaceState({}, document.title, window.location.pathname + window.location.hash)")
    }

    private fun clearSession() {
        linkRequestVersion++
        linkedWishlist = null
        linkedWishlistLoading = false
        storage.removeItem(STORAGE_KEY)
        api.setAccessToken(null)
    }

    private fun readableError(error: Throwable): String = when (error) {
        is ApiClientException -> error.error.message
        else -> error.message ?: "Не удалось выполнить запрос"
    }

    private fun parseMinorUnits(value: String): Long {
        val normalized = value.replace(',', '.').trim()
        require(normalized.matches(Regex("\\d+(\\.\\d{1,2})?"))) {
            "Цена должна быть числом, например 29.90"
        }
        val parts = normalized.split('.')
        val major = parts[0].toLong()
        val minor = parts.getOrNull(1).orEmpty().padEnd(2, '0').toLong()
        return major * 100 + minor
    }

    private fun parseBirthdayInput(value: String, yearKnown: Boolean): Birthday? {
        val normalized = value.trim()
        if (normalized.isEmpty()) return null
        val parts = normalized.split('-')
        require(parts.size == 3) { "Выберите дату рождения" }
        val parsedYear = parts[0].toIntOrNull() ?: error("Дата рождения указана неверно")
        val parsedMonth = parts[1].toIntOrNull() ?: error("Дата рождения указана неверно")
        val parsedDay = parts[2].toIntOrNull() ?: error("Дата рождения указана неверно")
        require(parsedMonth in 1..12 && parsedDay in 1..31) { "Дата рождения указана неверно" }
        return Birthday(parsedDay, parsedMonth, if (yearKnown) parsedYear else null)
    }

    fun close() {
        scope.cancel()
        api.close()
    }

    private companion object {
        const val STORAGE_KEY = "presentocracy.refreshToken"
    }
}

private fun webApiBaseUrl(): String {
    val hostname = js("window.location.hostname") as String
    return if (hostname == "localhost" || hostname == "127.0.0.1") {
        "http://127.0.0.1:8080"
    } else {
        // Staging and production are served with the API behind the same origin.
        ""
    }
}

private fun List<Wishlist>.replaceGift(wishlistId: String, gift: Gift): List<Wishlist> = map { wishlist ->
    if (wishlist.id == wishlistId) wishlist.copy(gifts = wishlist.gifts + gift) else wishlist
}

private fun List<Friendship>.upsertFriendship(friendship: Friendship): List<Friendship> =
    filterNot { it.id == friendship.id || it.user.id == friendship.user.id } + friendship
